///
/// EarthBrowser
///
/// IsobarLabelNode - Renders a pressure value label lying flat on the surface
/// along an isobar, matching the PressureCenterNode text style. The label is
/// rescaled every frame against the camera distance so it keeps a constant
/// on-screen size, and fades with camera altitude via a dynamic color uniform.
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { TextGeometry } from "@/earthbrowser/entities/TextNode/TextGeometry"
import { TextRenderer } from "@/earthbrowser/entities/TextNode/TextRenderer"
import { type Font } from "@/earthbrowser/libs/opentype.js"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type Renderer } from "@stovokor/engine/Renderer"
import { rgba, type color } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { dot, vec3 } from "@stovokor/math/vector"
import { type Material } from "@stovokor/shader/Material"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type IsobarLabel } from "./IsobarLabelExtractor"

const cLabelAltitudeKm = 5.0    // matches the isoline sphere and pressure centers
const cFontSize = 110           // geometry tessellation size; on-screen size comes from scaling
const cTargetPixelEm = 15       // desired on-screen em height in pixels
const cMaxScaleDistanceKm = 5000 // beyond this camera distance labels stop growing (world-fixed)
const cFacingFadeStart = 0.15   // fully hidden when surface normal is this oblique to the camera
const cFacingFadeEnd = 0.4      // fully visible above this facing ratio
/** Isobar label tint (light blue-white, like the isobar cores) */
export const cIsobarLabelColor: color = rgba(0.82, 0.88, 1.0, 1)

/** TextRenderer with the color as a per-frame dynamic uniform so alpha can fade */
class FadingTextRenderer extends TextRenderer {
	public color = new UniformDynamic<color>(cIsobarLabelColor)
}

class LabelMaterial implements Material {
	public transparency = true
	public available = true
}

class TextHolderNode extends NodeBase {
	constructor(renderer: Renderer) {
		super()
		this.renderer = renderer
	}
}

export class IsobarLabelNode extends NodeBase {
	private textHolder: TextHolderNode
	private textRenderer: FadingTextRenderer
	private textBase: matrix
	private worldPos: vec3 = vec3.zero
	private lastFade = 1
	private lastScale = 0
	private altitudeKm = NaN
	private readonly lonRad: number
	private readonly latRad: number
	private readonly tint: color

	/**
	 * @param altitudeKm Shell altitude the label sits on; movable later via
	 * setAltitude for shells that follow the camera
	 * @param tint Label color (alpha is driven by the fade)
	 * @param text Label text; defaults to the rounded contour value
	 */
	constructor(
		context: EarthBrowserContext,
		label: IsobarLabel,
		font: Font,
		altitudeKm: number = cLabelAltitudeKm,
		tint: color = cIsobarLabelColor,
		text: string = Math.round(label.pressureHpa).toString()
	) {
		super()
		this.tint = tint

		const geometry = new TextGeometry(context, text, font, cFontSize)
		this.textRenderer = new FadingTextRenderer(context, geometry, new LabelMaterial())
		this.textRenderer.color.value = rgba(tint.r, tint.g, tint.b, 1)

		// Lay the text flat on the surface (TextGeometry is in the XY plane)
		this.textBase = matrix.axisRotation(vec3.unitX, -trig.half_pi)
			.rotateBy(vec3.unitY, trig.pi)
		this.textHolder = new TextHolderNode(this.textRenderer)
		this.textHolder.transform = this.textBase
		this.addNode(this.textHolder)

		this.lonRad = label.lon * trig.rad
		this.latRad = label.lat * trig.rad
		this.setAltitude(altitudeKm)
	}

	/** Move the label to a new shell altitude (no-op when unchanged) */
	public setAltitude(altitudeKm: number): void {
		if (Math.abs(altitudeKm - this.altitudeKm) < 0.01) return
		this.altitudeKm = altitudeKm

		const geo = new geographic(this.lonRad, this.latRad, altitudeKm)
		this.worldPos = geo.cartesian
		const xRot = matrix.axisRotation(vec3.unitX, geo.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, geo.longitude + trig.pi)
		this.transform = matrix.translate(this.worldPos)
			.premultiply(yRot.premultiply(xRot))
	}

	/**
	 * Keeps the label at a constant on-screen size and applies the fade.
	 * @param cameraPos camera position in world space (km)
	 * @param worldPerPixelAtUnitDist world units per screen pixel at distance 1
	 * @param fade 0..1 opacity from the layer's altitude fade
	 */
	public updateScreenSpace(cameraPos: vec3, worldPerPixelAtUnitDist: number, fade: number): void {
		const toCamera = cameraPos.subtract(this.worldPos)
		const distance = toCamera.length

		// Fade labels out as their surface normal turns away from the camera,
		// which removes the pileup of foreshortened labels near the limb
		const facing = dot(this.worldPos, toCamera) / (this.worldPos.length * distance)
		const facingFade = Math.min(1, Math.max(0, (facing - cFacingFadeStart) / (cFacingFadeEnd - cFacingFadeStart)))

		const alpha = fade * facingFade
		this.visible = alpha > 0.01
		if (!this.visible) return

		if (alpha !== this.lastFade) {
			this.lastFade = alpha
			this.textRenderer.color.value = rgba(this.tint.r, this.tint.g, this.tint.b, alpha)
		}

		// Screen-constant size while close; capped so labels shrink with the
		// globe (world-fixed) once the camera is far away
		const scale = (cTargetPixelEm * Math.min(distance, cMaxScaleDistanceKm) * worldPerPixelAtUnitDist) / cFontSize
		if (Math.abs(scale - this.lastScale) > this.lastScale * 0.001) {
			this.lastScale = scale
			this.textHolder.transform = this.textBase.scaleBy(scale)
		}
	}
}
